// This phone (the director, 7 Oct and 3 Nov 2026): MouldOS on the home screen
// like an app, opening with no signal, and notifications. Android and desktop
// Chrome can install from a button; an iPhone is shown how (Share, Add to Home
// Screen), and only the app on its home screen can receive notifications.

import api from '../api';

let installPrompt = null;
const subs = new Set();
const emit = () => subs.forEach(f => f());
export function onPhoneChange(f) { subs.add(f); return () => subs.delete(f); }

// Before React: the browser offers installing once, early.
export function startPhone() {
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); installPrompt = e; emit(); });
  window.addEventListener('appinstalled', () => { installPrompt = null; emit(); });
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) return;
  // The way out if a worker ever misbehaves: /?nosw takes it off this device.
  if (window.location.search.includes('nosw')) {
    navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister()));
    return;
  }
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* the app works without it, online */ });
  });
  // A tap on a notification with MouldOS already open.
  navigator.serviceWorker.addEventListener('message', (e) => {
    if (e.data && e.data.type === 'mouldos:open' && e.data.link) window.location.href = e.data.link;
  });
}

const ua = () => navigator.userAgent || '';
export const isIos = () => /iphone|ipad|ipod/i.test(ua()) || (/macintosh/i.test(ua()) && navigator.maxTouchPoints > 1);
export const isInstalled = () => {
  try { return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true; } catch { return false; }
};
export const canInstall = () => !!installPrompt;
export async function install() {
  if (!installPrompt) return false;
  const p = installPrompt;
  installPrompt = null;
  p.prompt();
  const { outcome } = await p.userChoice;
  emit();
  return outcome === 'accepted';
}

export const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
// Why notifications cannot be turned on here, or null.
export function pushBlockedWhy() {
  if (isIos() && !isInstalled()) return 'ios-install';
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return null;
}

function keyBytes(base64) {
  const s = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
}
const send = (sub) => api.post('/api/v1/push/subscribe', { ...sub.toJSON(), device: ua().slice(0, 120) });

export async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}
// Turn on: throws with a sentence when it cannot be done.
export async function turnOnPush() {
  const asked = await Notification.requestPermission();
  if (asked !== 'granted') throw new Error('This phone said no. Notifications can be allowed again in its settings for MouldOS.');
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get('/api/v1/push/key');
  const sub = await reg.pushManager.getSubscription() || await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(data.key) });
  await send(sub);
  emit();
}
export async function turnOffPush() {
  const sub = await currentSubscription();
  if (sub) {
    await api.post('/api/v1/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe();
  }
  emit();
}
// A phone that said yes stays with whoever is signed in on it now.
export async function keepPushCurrent() {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const sub = await currentSubscription();
    if (sub) await send(sub);
  } catch { /* tried again next time the app opens */ }
}
